/**
 * Notes model for the Report Builder col-2 (Notes & Snippets).
 *
 * Notes are **snippets only** — OCR snips + text highlights mapped from
 * Assessment Prep and Assessment Scribe (the `/source-snippets` extracts).
 * AI/scribe summary blobs are dropped. Pure mapping/filter/count/sort
 * helpers live here so they're unit-testable without React.
 *
 * MDRS-8: the filter is a single "source surface" axis with four values
 * (Doc snippet / Doc summary / Scribe snippet / Scribe summary), derived from
 * the (target_field, extract_type, category_slug) triple rather than the
 * older origin × kind 2×2. See `sourceFromExtract`.
 */
import type {
  SectionSourceSnippets,
  SourceExtract,
} from "@hooks/queries/use-templates";

/**
 * The four surfaces a mapped snippet can come from. These drive both the
 * filter chips and each card's provenance label.
 *
 *   prep_snippet  "Doc snippet"    — Assessment Prep OCR text snip
 *   prep_summary  "Doc summary"    — Assessment Prep AI-summary highlight
 *   scribe_ai     "Scribe snippet" — Assessment Scribe summariser highlight
 *   scribe_text   "Scribe summary" — Assessment Scribe full-transcript highlight
 */
export type NoteSource =
  | "prep_snippet"
  | "prep_summary"
  | "scribe_ai"
  | "scribe_text";

export type ReportNote = {
  id: string;
  text: string;
  source: NoteSource;
  sourceFileName?: string | null;
  pageNumber?: number | null;
  categorySlug?: string | null;
  /** Owning section + module (for the "All sections" view + counts). */
  sectionKey: string;
  sectionLabel: string;
  moduleName: string;
  /** Per-(module,target_field) order from the backend — drives the section view. */
  order: number;
  /** Flat manual sort position for the All-Notes view; null until hand-sorted. */
  globalOrder: number | null;
};

/** The scribe `category_slug` whose highlights are verbatim transcript text. */
const FULL_TRANSCRIPT_SLUG = "full_transcript";

/**
 * Map an extract's (target_field, extract_type, category_slug) to its source
 * surface. Case-insensitive: the API may serialize the SqlEnum as the
 * lowercase value (`summary_from_assessment_scribe`) or the uppercase member
 * name (`SUMMARY_FROM_ASSESSMENT_SCRIBE`), so normalise before comparing.
 *
 *   Prep   (`summary_from_doc_sorter`):
 *     `snip`  → prep_snippet ("Doc snippet", OCR text)
 *     else    → prep_summary ("Doc summary", summary highlight)
 *   Scribe (`summary_from_assessment_scribe`):
 *     category_slug == full_transcript → scribe_text ("Scribe summary")
 *     else                             → scribe_ai   ("Scribe snippet", summariser)
 */
export const sourceFromExtract = (
  targetField: string,
  extractType: string,
  categorySlug: string | null | undefined
): NoteSource => {
  const isScribe = targetField.toLowerCase().includes("assessment_scribe");
  if (isScribe) {
    return (categorySlug ?? "").toLowerCase() === FULL_TRANSCRIPT_SLUG
      ? "scribe_text"
      : "scribe_ai";
  }
  return extractType.toLowerCase() === "snip" ? "prep_snippet" : "prep_summary";
};

/** Chip + provenance label for each source surface. */
export const SOURCE_LABEL: Record<NoteSource, string> = {
  prep_snippet: "Doc snippet",
  prep_summary: "Doc summary",
  scribe_ai: "Scribe snippet",
  scribe_text: "Scribe summary",
};

/** Filter-chip render order. */
export const NOTE_SOURCES: NoteSource[] = [
  "prep_snippet",
  "prep_summary",
  "scribe_ai",
  "scribe_text",
];

const mapExtract = (
  extract: SourceExtract,
  sectionKey: string,
  sectionLabel: string,
  moduleName: string
): ReportNote => ({
  id: extract.id,
  text: extract.text,
  source: sourceFromExtract(
    extract.target_field,
    extract.extract_type,
    extract.category_slug
  ),
  sourceFileName: extract.source_file_name,
  pageNumber: extract.page_number,
  categorySlug: extract.category_slug,
  sectionKey,
  sectionLabel,
  moduleName,
  order: extract.order,
  globalOrder: extract.global_order ?? null,
});

/**
 * Flatten `/source-snippets` into a single ReportNote[] tagged with its
 * section/module. `extracts` is the only source — summaries are ignored.
 */
export const flattenNotes = (sections: SectionSourceSnippets[]): ReportNote[] => {
  const out: ReportNote[] = [];
  for (const section of sections) {
    for (const [moduleName, mod] of Object.entries(section.modules)) {
      for (const extract of mod.extracts ?? []) {
        out.push(mapExtract(extract, moduleName, section.section_name, moduleName));
      }
    }
  }
  return out;
};

/** Notes for a single section/module (the "This section" tab). */
export const notesForSection = (
  notes: ReportNote[],
  sectionKey: string | null
): ReportNote[] =>
  sectionKey ? notes.filter((n) => n.sectionKey === sectionKey) : [];

/** Per-section note counts → drives the col-1 badge (`extracts.length`). */
export const countNotesBySection = (notes: ReportNote[]): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const n of notes) {
    counts[n.sectionKey] = (counts[n.sectionKey] ?? 0) + 1;
  }
  return counts;
};

/**
 * Order for the All-Notes view: hand-sorted notes (those with a
 * `globalOrder`) come first in ascending order; notes never hand-sorted
 * keep their input order after them. Stable — a fully-unsorted list is
 * returned unchanged, so it falls back to the backend's flatten order.
 */
export const sortNotesForAllView = (notes: ReportNote[]): ReportNote[] =>
  notes
    .map((note, index) => ({ note, index }))
    .sort((a, b) => {
      const ag = a.note.globalOrder;
      const bg = b.note.globalOrder;
      if (ag === null && bg === null) return a.index - b.index;
      if (ag === null) return 1;
      if (bg === null) return -1;
      if (ag !== bg) return ag - bg;
      return a.index - b.index;
    })
    .map(({ note }) => note);

export type NoteFilters = {
  sources: NoteSource[];
  search: string;
};

export const EMPTY_FILTERS: NoteFilters = {
  sources: [],
  search: "",
};

/**
 * Filter notes by the source-type axis + search.
 * AND across the two; OR within the source axis; an empty axis = no constraint.
 */
export const filterNotes = (
  notes: ReportNote[],
  filters: NoteFilters
): ReportNote[] => {
  const q = filters.search.trim().toLowerCase();
  return notes.filter((n) => {
    if (filters.sources.length && !filters.sources.includes(n.source)) return false;
    if (q && !n.text.toLowerCase().includes(q)) return false;
    return true;
  });
};
